import { useEffect, useRef, useState } from "react";
import { Download, ExternalLink, X } from "lucide-react";
import { RequestError } from "@/components/RequestError";
import { Button, buttonClass } from "@/components/ui/button";
import { saveBlob } from "@/lib/files";
import { shell } from "@/lib/platform";

export type ViewerFile = { blob: Blob; filename: string };

type DocumentViewerProps = {
  title: string;
  /** Fetches the file; called once, when the viewer opens. */
  load: () => Promise<ViewerFile>;
  onClose: () => void;
  /** The file's own page outside the app (its Drive page), offered as "Open in Drive". */
  externalUrl?: string;
};

// Browsers show these themselves; HEIC only Safari does, so it is downloaded instead.
const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * A bill, a payment proof or a PDF preview over the page: a PDF in the browser's own viewer,
 * a photo as an image. Android's WebView has no PDF viewer, so there the file is handed to
 * the phone's viewer instead.
 */
export function DocumentViewer({ title, load, onClose, externalUrl }: DocumentViewerProps) {
  const [file, setFile] = useState<ViewerFile | null>(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState<unknown>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const loadOnce = useRef(load);

  useEffect(() => {
    let live = true;
    let objectUrl = "";
    loadOnce
      .current()
      .then((loaded) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(loaded.blob);
        setFile(loaded);
        setUrl(objectUrl);
      })
      .catch((err: unknown) => live && setError(err));
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // Focus moves in once; a parent re-render (a new onClose) must not pull it back.
  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const download = file ? () => void saveBlob(file.blob, file.filename) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#071a3f]/70 p-2 backdrop-blur-[2px] sm:p-6"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border bg-card shadow-md"
      >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
          <h2 className="min-w-0 truncate text-base font-semibold">{title}</h2>
          <div className="flex shrink-0 items-center gap-2">
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonClass("outline", "sm")}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Open in Drive
              </a>
            )}
            {download && (
              <Button size="sm" variant="outline" onClick={download}>
                <Download className="h-3.5 w-3.5" aria-hidden />
                Download
              </Button>
            )}
            <Button ref={closeButton} size="sm" variant="ghost" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-muted">
          <ViewerBody title={title} file={file} url={url} error={error} download={download} />
        </div>
      </div>
    </div>
  );
}

function ViewerBody({
  title,
  file,
  url,
  error,
  download,
}: {
  title: string;
  file: ViewerFile | null;
  url: string;
  error: unknown;
  download?: () => void;
}) {
  if (error) {
    return (
      <div className="max-w-md p-4">
        <RequestError error={error} />
      </div>
    );
  }
  if (!file || !url) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const type = file.blob.type;
  if (type === "application/pdf" && shell() !== "android") {
    return <iframe title={title} src={url} className="h-full w-full border-0 bg-white" />;
  }
  if (IMAGE_TYPES.has(type)) {
    return <img src={url} alt={title} className="max-h-full max-w-full object-contain" />;
  }
  return (
    <div className="flex flex-col items-center gap-3 p-6 text-center">
      <p className="text-sm text-muted-foreground">
        {shell() === "android"
          ? "This file opens in the phone's own viewer."
          : "This file cannot be shown here; download it to open it."}
      </p>
      {download && (
        <Button size="sm" onClick={download}>
          <Download className="h-4 w-4" aria-hidden />
          {shell() === "android" ? "Open" : "Download"}
        </Button>
      )}
    </div>
  );
}
